<template>
  <div class="card-traceability">
    <card
      v-if="card.visible"
      :id="card.id"
      :key="card.id"
      :title="card.title"
      :capitalize-subtitle="card.capitalizeSubtitle"
      :transparent-card="card.transparentCard"
      :subtitle-regular="getUpdateTime.phrase"
      :subtitle-highlight="getUpdateTime.time + ' (Período 7 dias)'"
      :buttons="card.buttons"
      :class="[{ 'card-disabled': card.disabled }, card.class]"
    >
      <div
        v-if="isLoadingCounters"
        class="spinner-container"
      >
        <spinner class="medium" />
      </div>
      <component
        :is="card.component.name"
        v-else-if="card.component"
        :key="card.component.key"
        :content="content"
        class="mt-4"
      />
      <infotip
        v-if="card.disabled"
        :id="card.id + '-disabled'"
        :key="card.id + '-infdotip-disabled'"
        :text="
          card.disabledMessage || 'A funcionalidade está temporariamente em manutenção.'
        "
        :auto-open-on-first-days="false"
        :never-open-again-after-dismissed="false"
        :close-on-mouse-leave="true"
      />
    </card>
  </div>
</template>

<script>
import Card from '../Card'
import Infotip from '@/components/common/infotip/Infotip'
import GraphicData from '../Widgets/GraphicData'
import Spinner from '@/components/common/Spinner'
import { mapGetters } from 'vuex'

const NTO_MESSAGE_SINGULAR = ''
const NTO_MESSAGE_PLURAL = ''

export default {
  name: 'CardTraceability',
  components: {
    card: Card,
    infotip: Infotip,
    'graphic-data': GraphicData,
    spinner: Spinner,
  },
  computed: {
    ...mapGetters([
      'getUpdateTime',
      'totalSamplesWaitingForTransport',
      'totalSamplesInExternalTraceability',
      'samplesPerDestinations',
      'traceabilityGroupedExamsCount',
      'toggle',
      'loadingStatusFromTotalCounters',
      'loadingStatusFromSamplesPerPlaceExecution',
      'traceabilityCountersLoadingStatus',
    ]),
    isLoadingCounters() {
      return (
        this.loadingStatusFromTotalCounters === 'STATUS_LOADING' ||
        this.loadingStatusFromSamplesPerPlaceExecution === 'STATUS_LOADING' ||
        this.traceabilityCountersLoadingStatus === 'STATUS_LOADING'
      )
    },
    card() {
      return {
        id: 'traceability',
        visible: true,
        disabled: !this.toggle.sampleTraceability,
        title: 'Rastreabilidade - logística',
        updateTimeVisible: true,
        class: 'logistic',
        component: {
          name: 'graphic-data',
          key: 1,
          content: {
            graphicType: 'doughnut',
            total: 0,
            chartdata: {
              datacollection: {
                datasets: [
                  {
                    data: [
                      this.totalSamplesWaitingForTransport,
                      ...Array(this.samplesPerDestinations.length).fill(0),
                    ],
                    backgroundColor: ['#00B5CC', '#F2994A', '#55CD9E', '#8AD4DF'],
                  },
                ],
              },
            },
            options: {
              responsive: true,
              cutoutPercentage: 88,
              legend: {
                display: false,
              },
              weigth: 0,
              tooltips: {
                enabled: false,
              },
            },
            items: [],
            NTOsLeft: [],
          },
        },
      }
    },
    content() {
      let items = [
        {
          visible: true,
          number:
            this.toggle.sampleTraceability === true
              ? (this.traceabilityGroupedExamsCount?.CADASTRADO ?? 0)
              : 0,
          text: 'aguardando retirada',
          traceabilityStatus: 'CADASTRADO',
          color: 'blue',
          sizeBig: true,
        },
      ]
      let colors = ['yellow', 'dark-orange', 'morning-glory', 'blue', 'red', 'green']
      let colorsRef = ['#fe9c4b', '#ee5c53', '#8ad4df', '#3cc2d9', '#e2245a', '#68edae']
      for (let i = 0; i < this.samplesPerDestinations.length; i++) {
        items.push({
          visible: false,
          number: 0,
          text: '',
          traceabilityStatus: 'EM_TRANSITO',
          color: colors[i % colors.length],
          colorRef: colorsRef[i % colors.length],
          sizeBig: true,
        })
      }

      let data = {
        graphicType: 'doughnut',
        total:
          this.toggle.sampleTraceability === true
            ? (this.traceabilityGroupedExamsCount?.CADASTRADO ?? 0)
            : 0,
        chartdata: {
          datacollection: {
            labels: [
              'Aguardando Retirada',
              ...this.samplesPerDestinations.map(
                destination => destination.placeExecution || 'Indefinido'
              ),
            ],
            datasets: [
              {
                data: [
                  this.toggle.sampleTraceability === true
                    ? (this.traceabilityGroupedExamsCount?.CADASTRADO ?? 0)
                    : 0,
                  ...Array(this.samplesPerDestinations.length).fill(0),
                ],
                backgroundColor: items.map(item => item.colorRef),
              },
            ],
          },
        },
        options: {
          responsive: true,
          cutoutPercentage: 88,
          legend: {
            display: false,
          },
          tooltips: {
            enabled: true,
            callbacks: {
              label: function (tooltipItem, data) {
                const dataset = data.datasets[tooltipItem.datasetIndex]
                const dataIndex = tooltipItem.index
                const value = dataset.data[dataIndex]
                const label = data.labels ? data.labels[dataIndex] : ''
                return `${label}: ${value}`
              },
            },
          },
        },
        items: items,
        NTOsLeft: [],
      }
      return data
    },
    totalExternalTraceability: function () {
      if (this.totalSamplesInExternalTraceability > 999) {
        return (
          Math.sign(this.totalSamplesInExternalTraceability) *
            (Math.abs(this.totalSamplesInExternalTraceability) / 1000).toFixed(1) +
          'k'
        )
      } else {
        return this.totalSamplesInExternalTraceability
      }
    },
  },
  watch: {
    samplesPerDestinations: function () {
      this.populatePlaceExecutions()
    },
    traceabilityGroupedExamsCount: function () {
      this.populatePlaceExecutions()
    },
    totalSamplesInExternalTraceability: function (newVal) {
      this.populatePlaceExecutions()
    },
  },
  methods: {
    populatePlaceExecutions() {
      if (!this.toggle.sampleTraceability) {
        return
      }

      let waitingForCollect = this.content.items[0]
      let graphic = this.content.chartdata.datacollection
      let totalPlaceExecutions = this.samplesPerDestinations.length
      let MAXIMUM_NTO_TO_SHOW = totalPlaceExecutions + 1
      let content = this.content

      const cadastradoCount = this.traceabilityGroupedExamsCount?.CADASTRADO ?? 0
      waitingForCollect.number = cadastradoCount
      waitingForCollect.visible = true
      graphic.datasets[0].data[0] = cadastradoCount

      if (totalPlaceExecutions > 0) {
        for (let i = 1; i <= MAXIMUM_NTO_TO_SHOW; i++) {
          let j = i - 1
          let totalSamples = this.samplesPerDestinations[j]?.samplesCount
          let placeExecution = this.samplesPerDestinations[j]?.placeExecution
          let message = totalSamples > 1 ? NTO_MESSAGE_PLURAL : NTO_MESSAGE_SINGULAR
          let item = this.content.items[i]

          if (item) {
            item.visible = true
            item.text = message + placeExecution
            item.placeExecution = placeExecution
            item.number = totalSamples
            graphic.datasets[0].data[i] = totalSamples
          }
        }
      } else {
        graphic.datasets[0].data[1] = 0
        graphic.datasets[0].data[2] = 0
        graphic.datasets[0].data[3] = 0
      }
      if (totalPlaceExecutions > MAXIMUM_NTO_TO_SHOW) {
        content.NTOsLeft = this.samplesPerDestinations.slice(MAXIMUM_NTO_TO_SHOW)
      }

      const otherDestinationsTotal = this.samplesPerDestinations.reduce(
        (total, dest) => total + (dest.samplesCount || 0),
        0
      )
      content.total = cadastradoCount + otherDestinationsTotal

      this.card.component.key += 1
    },
  },
}
</script>

<style lang="scss">
@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  .card-traceability {
    flex: 1;
  }
}

@media (max-width: 1080px) {
  .card-traceability {
    margin-bottom: 40px;
  }
}

.logistic {
  .graphic-legend {
    overflow-y: scroll;
    height: 235px;
    width: 100% !important;
    margin-left: 40px !important;
  }
  .count-indicator__count {
    &.count-indicator__count--bigger {
      p {
        font-size: 24px;
      }
    }
  }
  .count-indicator__description--bigger {
    width: 100% !important;
  }
}

.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 250px;
  width: 100%;
}
</style>
